import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
  type CognitoUserSession,
  type IAuthenticationCallback,
} from "amazon-cognito-identity-js";

import { type AppUser } from "./types";

const POOL_ID = import.meta.env.VITE_COGNITO_POOL_ID;
const CLIENT_ID = import.meta.env.VITE_COGNITO_CLIENT_ID;

const userPool = new CognitoUserPool({ UserPoolId: POOL_ID, ClientId: CLIENT_ID });

/** Pending sign-in state. The SDK attaches challenge context (SRP keys, session) to the `CognitoUser` instance, so we
 *  keep the same instance across a multi-step flow (password → MFA / new-password → success). Cleared on every fresh
 *  `signIn()` call and on `signOut()`.
 */
let pendingUser: CognitoUser | null = null;
let pendingNewPasswordAttrs: Record<string, string> | null = null;
let pendingMfaType: MfaType = "SMS_MFA";

export type MfaType = "SMS_MFA" | "SOFTWARE_TOKEN_MFA";

export type AuthStep =
  | { kind: "success"; user: AppUser }
  | { kind: "mfa"; mfaType: MfaType }
  | { kind: "newPassword" };

export class AuthError extends Error {
  readonly code: string;
  constructor(code: string, message: string) {
    super(message);
    this.name = "AuthError";
    this.code = code;
  }
}

const sessionCallbacks = (resolve: (step: AuthStep) => void, reject: (err: AuthError) => void): IAuthenticationCallback => ({
  onSuccess: (session) => resolve({ kind: "success", user: userFromSession(session) }),
  onFailure: (err: unknown) => reject(toAuthError(err)),
  mfaRequired: () => {
    pendingMfaType = "SMS_MFA";
    resolve({ kind: "mfa", mfaType: "SMS_MFA" });
  },
  totpRequired: () => {
    pendingMfaType = "SOFTWARE_TOKEN_MFA";
    resolve({ kind: "mfa", mfaType: "SOFTWARE_TOKEN_MFA" });
  },
  newPasswordRequired: (userAttributes: Record<string, string>) => {
    // Cognito rejects these two on completeNewPasswordChallenge even though it sends them to us. Drop them.
    delete userAttributes.email_verified;
    delete userAttributes.phone_number_verified;
    pendingNewPasswordAttrs = userAttributes;
    resolve({ kind: "newPassword" });
  },
});

export const signIn = (email: string, password: string): Promise<AuthStep> =>
  new Promise((resolve, reject) => {
    pendingUser = new CognitoUser({ Username: email.trim(), Pool: userPool });
    pendingNewPasswordAttrs = null;
    const authDetails = new AuthenticationDetails({ Username: email.trim(), Password: password });
    pendingUser.authenticateUser(authDetails, sessionCallbacks(resolve, reject));
  });

export const respondMfa = (code: string): Promise<AuthStep> =>
  new Promise((resolve, reject) => {
    if (!pendingUser) return reject(new AuthError("NoPendingSignIn", "No sign-in in progress"));
    pendingUser.sendMFACode(code.trim(), sessionCallbacks(resolve, reject), pendingMfaType);
  });

export const completeNewPassword = (newPassword: string): Promise<AuthStep> =>
  new Promise((resolve, reject) => {
    if (!pendingUser) return reject(new AuthError("NoPendingSignIn", "No sign-in in progress"));
    pendingUser.completeNewPasswordChallenge(newPassword, pendingNewPasswordAttrs ?? {}, sessionCallbacks(resolve, reject));
  });

/** Return the current user if a valid cached session exists in localStorage, otherwise null. Called once on app mount
 *  to avoid forcing re-login after a page refresh.
 */
export const restoreSession = (): Promise<AppUser | null> =>
  new Promise((resolve) => {
    const cached = userPool.getCurrentUser();
    if (!cached) return resolve(null);
    cached.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session?.isValid()) return resolve(null);
      resolve(userFromSession(session));
    });
  });

/** Fetch a valid ID token, auto-refreshing via the cached refresh token if the current token is close to / past
 *  expiry. Resolves null if no session exists or the refresh failed (user should be logged out).
 */
export const getValidToken = (): Promise<string | null> =>
  new Promise((resolve) => {
    const cached = userPool.getCurrentUser();
    if (!cached) return resolve(null);
    cached.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session?.isValid()) return resolve(null);
      resolve(session.getIdToken().getJwtToken());
    });
  });

/** Fetch a valid access token. Used when our BE needs to proxy a call to Emmy — Emmy's `bearerAuth` expects the
 *  Cognito AccessToken, not the ID token (our own verifier wants the ID token so it can read user claims). This is
 *  attached as `X-Emmy-Token` alongside the normal ID-token Authorization header; see `api.authHeaders`.
 */
export const getValidAccessToken = (): Promise<string | null> =>
  new Promise((resolve) => {
    const cached = userPool.getCurrentUser();
    if (!cached) return resolve(null);
    cached.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session?.isValid()) return resolve(null);
      resolve(session.getAccessToken().getJwtToken());
    });
  });

export const signOut = () => {
  userPool.getCurrentUser()?.signOut();
  pendingUser = null;
  pendingNewPasswordAttrs = null;
};

const userFromSession = (session: CognitoUserSession): AppUser => {
  const claims = session.getIdToken().payload as Record<string, unknown>;
  const groups = Array.isArray(claims["cognito:groups"]) ? (claims["cognito:groups"] as string[]) : [];
  return {
    sub: String(claims.sub ?? ""),
    email: String(claims.email ?? ""),
    givenName: claims.given_name ? String(claims.given_name) : undefined,
    familyName: claims.family_name ? String(claims.family_name) : undefined,
    phoneNumber: claims.phone_number ? String(claims.phone_number) : undefined,
    groups,
  };
};

const toAuthError = (err: unknown): AuthError => {
  if (err && typeof err === "object") {
    const e = err as { code?: string; name?: string; message?: string };
    return new AuthError(e.code ?? e.name ?? "UnknownError", e.message ?? "Sign-in failed");
  }
  return new AuthError("UnknownError", String(err));
};
